"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { FeaturedPostsCarousel } from "@/views/home/parts/FeaturedPostsCarousel";
import { CategorySection } from "@/views/home/parts/CategorySection";
import { PromoSection } from "@/views/home/parts/PromoSection";
import { LatestPostsGrid } from "@/views/home/parts/LatestPostsGrid";
import { AdSlot } from "@/features/ads/AdSlot";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import type { NewsBlogHomeClassicData } from "@/views/home/variants/NewsBlogHomepage/Classic/types";
import type { BrandedStyle } from "@/components/layout/SectionStrip";
import { SectionStrip } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_COMPETITIONS } from "@/features/theme/sectionKeys";
export interface BasicHomeDefaultLayoutProps {
  data: NewsBlogHomeClassicData;
  videoShortsSlot?: React.ReactNode;
  videoHighlightsSlot?: React.ReactNode;
  sectionStyles?: Record<string, BrandedStyle>;
}

/**
 * BasicHomeDefaultLayout — Default homepage for the Basic site type.
 *
 * Content-only layout — no competition sections, livescore widget, or matches ticker.
 *
 * Sections (top to bottom):
 * 1. Featured Posts Carousel (hero spotlight)
 * 2. Mixed sections: category and promo kinds only (competition items silently skipped)
 * 3. Video Shorts carousel
 * 4. Video Highlights carousel
 * 5. Latest Posts Grid — shown only when no sections and no featured posts are configured
 */
export function BasicHomeDefaultLayout(t0) {
  const $ = _c(45);
  const {
    data,
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles
  } = t0;
  let showLatestFallback;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== data.featuredPosts || $[1] !== data.latestNews || $[2] !== data.promos[0] || $[3] !== data.sections || $[4] !== sectionStyles || $[5] !== videoShortsSlot) {
    const contentSections = data.sections.filter(_temp);
    const hasSections = contentSections.length > 0;
    const hasFeatured = data.featuredPosts.length > 0;
    showLatestFallback = !hasSections && !hasFeatured;
    t8 = "min-h-screen bg-background";
    t1 = "max-w-350 mx-auto px-4 py-6 space-y-10";
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AdSlot id="content-top" />;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    if ($[16] !== data.featuredPosts || $[17] !== data.latestNews || $[18] !== hasFeatured || $[19] !== sectionStyles) {
      t3 = (hasFeatured || data.latestNews.length > 0) && <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}><FeaturedPostsCarousel featuredPosts={data.featuredPosts} latestPosts={data.latestNews} priority={true} /></SectionStrip>;
      $[16] = data.featuredPosts;
      $[17] = data.latestNews;
      $[18] = hasFeatured;
      $[19] = sectionStyles;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    const t9 = data.promos[0] ?? null;
    if ($[21] !== t9) {
      t4 = <PromoSlot row={t9} />;
      $[21] = t9;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AdSlot id="mid-scroll-1" />;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    t6 = videoShortsSlot;
    t7 = hasSections && <SectionStrip brandedStyle={sectionStyles?.[HOME_COMPETITIONS]}><div className="space-y-10">{contentSections.map(_temp2)}</div></SectionStrip>;
    $[0] = data.featuredPosts;
    $[1] = data.latestNews;
    $[2] = data.promos[0];
    $[3] = data.sections;
    $[4] = sectionStyles;
    $[5] = videoShortsSlot;
    $[6] = showLatestFallback;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    showLatestFallback = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AdSlot id="mid-scroll-2" />;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = data.promos[1] ?? null;
  let t11;
  if ($[25] !== t10) {
    t11 = <PromoSlot row={t10} />;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== data.latestNews || $[28] !== showLatestFallback) {
    t12 = showLatestFallback && <LatestPostsGrid posts={data.latestNews} />;
    $[27] = data.latestNews;
    $[28] = showLatestFallback;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <AdSlot id="content-bottom" />;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t1 || $[32] !== t11 || $[33] !== t12 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== videoHighlightsSlot) {
    t14 = <main className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t9}{videoHighlightsSlot}{t11}{t12}{t13}</main>;
    $[31] = t1;
    $[32] = t11;
    $[33] = t12;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = videoHighlightsSlot;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t14 || $[43] !== t8) {
    t15 = <div className={t8}>{t14}</div>;
    $[42] = t14;
    $[43] = t8;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
}
function _temp2(section, i) {
  if (section.kind === "category") {
    return <CategorySection key={`cat-${section.data.categoryId}-${i}`} data={section.data} />;
  }
  if (section.kind === "promo") {
    return <PromoSection key={`promo-${i}`} promo={section.data} />;
  }
  return null;
}
function _temp(s) {
  return s.kind === "category" || s.kind === "promo";
}